import type { RowTone, ToneStyles } from './NutritionReportSectionTable';

/** Same palette as `NutritionReportTable` suggested RDA (`suggestedRdaToneStyles`). */
export const suggestedRdaToneStyles: ToneStyles = {
  met: {
    label: 'Normal',
    color: '#059669',
    bg: '#ECFDF5',
    bar: '#10b981',
  },
  borderline: {
    label: 'Borderline',
    color: '#B54708',
    bg: '#FFFAEB',
    bar: '#F79009',
  },
  deficient: {
    label: 'Below Target',
    color: '#D92D20',
    bg: '#FEF3F2',
    bar: '#F04438',
  },
  excess: {
    label: 'Above Target',
    color: '#D92D20',
    bg: '#FEF3F2',
    bar: '#F04438',
  },
  toxic: {
    label: 'Toxic',
    color: '#D92D20',
    bg: '#FEF3F2',
    bar: '#F04438',
  },
  na: {
    label: 'N/A',
    color: '#667085',
    bg: '#F2F4F7',
    bar: '#D0D5DD',
  },
};

/**
 * Same rules as Suggested RDA rows in `NutritionReportTable`.
 * For nutrients where lower is better (e.g. sugar), pass `preferLower: true`.
 */
export function getSuggestedRdaRowTone(current: number, recommended: number, preferLower: boolean): RowTone {
  if (recommended <= 0) return 'na';

  if (preferLower) {
    const progress = (current / recommended) * 100;
    if (progress <= 100) return 'met';
    if (progress <= 115) return 'borderline';
    return 'excess';
  }

  const percentageDiff = Math.abs((current - recommended) / recommended) * 100;

  if (percentageDiff <= 10) {
    return 'met';
  }
  if (percentageDiff <= 15) {
    return 'borderline';
  }
  if (current < recommended) {
    return 'deficient';
  }
  return 'excess';
}

/** AMDR gram band (e.g. `amdrLowGrams`–`amdrHighGrams`); same ±10% / ±15% bands as single-target rows. */
export function getSuggestedRdaRowToneForGramRange(
  current: number,
  lowGrams: number,
  highGrams: number,
): RowTone {
  if (lowGrams <= 0 || highGrams <= 0 || lowGrams > highGrams) return 'na';

  if (current >= lowGrams && current <= highGrams) return 'met';

  if (current < lowGrams) {
    const percentageDiff = ((lowGrams - current) / lowGrams) * 100;
    if (percentageDiff <= 10) return 'borderline';
    if (percentageDiff <= 15) return 'borderline';
    return 'deficient';
  }

  const percentageDiff = ((current - highGrams) / highGrams) * 100;
  if (percentageDiff <= 10) return 'borderline';
  if (percentageDiff <= 15) return 'borderline';
  return 'excess';
}

/** Map % of target to row tone (inverse of `getSuggestedRdaRowTone` for bar colors). */
export function getSuggestedRdaToneFromPercent(percent: number, preferLower = false): RowTone {
  if (!Number.isFinite(percent)) return 'na';
  if (percent === 0 && !preferLower) return 'deficient';

  if (preferLower) {
    if (percent <= 100) return 'met';
    if (percent <= 115) return 'borderline';
    return 'excess';
  }

  const percentageDiff = Math.abs(percent - 100);
  if (percentageDiff <= 10) return 'met';
  if (percentageDiff <= 15) return 'borderline';
  if (percent < 100) return 'deficient';
  return 'excess';
}

/**
 * Minimum-target nutrients (fibre, calcium, etc.): met near 100%;
 * below minimum is deficient; above it is met unless a separate upper limit exists.
 */
export function getSuggestedRdaToneFromPercentMinimumTarget(percent: number): RowTone {
  if (!Number.isFinite(percent)) return 'na';

  return percent >= 100 ? 'met' : 'deficient';
}

/** Progress bar color aligned with Suggested RDA rows in `NutritionReportTable`. */
export function getSuggestedRdaBarColorFromPercent(percent: number, isMinimumTarget = false): string {
  const tone = isMinimumTarget
    ? getSuggestedRdaToneFromPercentMinimumTarget(percent)
    : getSuggestedRdaToneFromPercent(percent, false);
  return suggestedRdaToneStyles[tone].bar;
}

const TONE_LABELS: Record<RowTone, string> = {
  met: 'On target',
  borderline: 'Near target',
  deficient: 'Below target',
  excess: 'Over target',
  toxic: 'Toxic',
  na: 'No target',
};

/** Color + label for intake-vs-target UI (KPI strip, macro cards, analysis tab). */
export function getSuggestedRdaIntakeStatus(percent: number): { color: string; label: string; tone: RowTone } {
  const tone = getSuggestedRdaToneFromPercent(percent, false);
  return {
    color: suggestedRdaToneStyles[tone].bar,
    label: TONE_LABELS[tone],
    tone,
  };
}

/** Minimum-target nutrients (fibre, calcium, etc.) — same rules as meal-plan drawer macro rings. */
export function getSuggestedRdaMinimumTargetIntakeStatus(percent: number): { color: string; label: string; tone: RowTone } {
  const tone = getSuggestedRdaToneFromPercentMinimumTarget(percent);
  return {
    color: suggestedRdaToneStyles[tone].bar,
    label: TONE_LABELS[tone],
    tone,
  };
}

/** Value + target — same tone/colors as meal-plan day card macro trends. */
export function getSuggestedRdaIntakeStatusFromTarget(
  value: number,
  target: number,
): { color: string; label: string; tone: RowTone } {
  const trend = getSuggestedRdaIntakeTrend(value, target);
  return {
    color: trend.barColor,
    label: TONE_LABELS[trend.tone],
    tone: trend.tone,
  };
}

/** Trend label + colors from absolute intake vs target (meal-plan macro cards). */
export function getSuggestedRdaIntakeTrend(value: number, target: number): {
  label: string;
  color: string;
  barColor: string;
  tone: RowTone;
} {
  const tone: RowTone = target <= 0 ? 'na' : getSuggestedRdaRowTone(value, target, false);
  const styles = suggestedRdaToneStyles[tone];
  const progress = target > 0 ? (value / target) * 100 : null;
  const label =
    progress == null || !Number.isFinite(progress)
      ? '—'
      : `${Math.max(0, Math.min(999, Math.round(progress)))}%`;

  return {
    label,
    color: styles.bar,
    barColor: styles.bar,
    tone,
  };
}

export type SuggestedRdaTrendIconKind = 'down' | 'up' | 'flat';

export function getSuggestedRdaTrendIconKind(tone: RowTone): SuggestedRdaTrendIconKind {
  if (tone === 'deficient') return 'down';
  if (tone === 'excess' || tone === 'toxic') return 'up';
  return 'flat';
}

/** % Met for AMDR gram band: 100% when inside range; otherwise vs nearest bound. */
export function getSuggestedRdaProgressForGramRange(
  current: number,
  lowGrams: number,
  highGrams: number,
): number {
  if (lowGrams <= 0 || highGrams <= 0) return 0;
  if (current >= lowGrams && current <= highGrams) return 100;
  if (current < lowGrams) return (current / Math.max(1, lowGrams)) * 100;
  return (current / Math.max(1, highGrams)) * 100;
}
